import type { FC } from 'react';
import { useState } from 'react';
import { Show, ShowFirstMatching } from '@components/Show';
import { baseBlockBodyClasses } from '@ContractBuilder/modules/block/classes';
import { rightPanelAnimationProps } from '@ContractBuilder/modules/drawer-layout/views/DrawerLayout';
import { TOOLBAR_HEIGHT } from '@ContractBuilder/settings';
import { ActivityLoading } from '@features/activity';
import { deriveUsers } from '@features/activity/utils/derive-users';
import { groupByDay } from '@features/activity/utils/group-by-day';
import { mapAuditItemUser } from '@features/activity/utils/map-audit-item-user';
import { ActivityControls, type ActivitySort, EVERYONE } from '@features/activity/views/ActivityControls';
import { ActivityEntry } from '@features/activity/views/ActivityEntry';
import { BlockConfigurationPanelController } from '@features/block-configuration-panel';
import { ClauseDocxImportControl, useReplaceClauseContentOutcome } from '@features/clause-docx-import';
import { EditorToolbarController } from '@features/editor-toolbar';
import { XIcon } from '@heroicons/react/outline';
import { INLINE_BLOCK_EDITOR_NAME } from '@providers/editors-map';
import { useAllTeams } from '@queries/fetchTeams';
import type { AuditEntry } from '@root/@types/audit-log';
import type { DynamoDBLibraryBlock } from '@root/@types/database';
import { getFeatureFlag } from '@root/helpers/feature-flags/get-feature-flag';
import { isClausesPath } from '@utils/app-paths';
import { clsx } from 'clsx';
import { AnimatePresence, motion } from 'motion/react';

import { MAX_WIDTH_STYLE } from '../constants';

import { LibraryBlockEditFooter } from './LibraryBlockEditFooter';
import { LibraryBlockEditHeader } from './LibraryBlockEditHeader';
import { LibraryBlockView } from './LibraryBlockView';

interface LibraryBlockEditLayoutProps {
  canNavigate: boolean;
  data?: DynamoDBLibraryBlock;
  blockAudit?: AuditEntry[];
  hasChanges: boolean;
  isActivityLogOpen: boolean;
  isLoadingBlockAudit: boolean;
  onUpdate: () => Promise<any> | void;
  toggleVisibilityOfActivityLog: () => void;
}

export const LibraryBlockEditLayout: FC<LibraryBlockEditLayoutProps> = ({
  canNavigate,
  data,
  blockAudit,
  hasChanges,
  isActivityLogOpen,
  isLoadingBlockAudit,
  onUpdate,
  toggleVisibilityOfActivityLog,
}) => {
  const isActivityLogEnabled = getFeatureFlag('LibraryBlockActivityLog');
  const isClause = isClausesPath();
  const replaceClauseContent = useReplaceClauseContentOutcome();
  const { data: teams } = useAllTeams();
  const [filterKey, setFilterKey] = useState(EVERYONE);
  const [sort, setSort] = useState<ActivitySort>('newest');

  if (!data) {
    return null;
  }

  return (
    <div className="bg-info-50 relative flex w-full flex-col">
      <div className="relative flex flex-col">
        <LibraryBlockEditHeader
          canNavigate={canNavigate}
          data={data}
          isActivityLogOpen={isActivityLogOpen}
          onUpdate={onUpdate}
          toggleVisibilityOfActivityLog={toggleVisibilityOfActivityLog}
        />

        <div className={clsx('relative z-30 flex w-full bg-white', TOOLBAR_HEIGHT)}>
          <div className="relative w-full border-b">
            <EditorToolbarController
              beforeInsertControl={
                isClause && getFeatureFlag('ClauseDocxImport') ? (
                  <ClauseDocxImportControl key="clause-docx-import" outcome={replaceClauseContent} />
                ) : undefined
              }
              currentEditorKey={INLINE_BLOCK_EDITOR_NAME}
              type="contract"
            />
          </div>
        </div>

        <div className="flex w-full" style={{ height: 'calc(100vh - 111px)' }}>
          <div className="flex flex-1 flex-col justify-between" style={{ maxWidth: MAX_WIDTH_STYLE }}>
            <div className={clsx('mx-auto mt-4 flex w-full max-w-32 justify-center px-0! pb-16', baseBlockBodyClasses)}>
              <LibraryBlockView block={data} onUpdate={onUpdate} />
            </div>
            <LibraryBlockEditFooter hasChanges={hasChanges} onUpdate={onUpdate} />
          </div>
          <div className="relative w-[320px] overflow-hidden">
            <AnimatePresence initial={false}>
              {isActivityLogOpen && isActivityLogEnabled ? (
                <motion.div
                  key="activity"
                  {...rightPanelAnimationProps}
                  className="absolute top-0 right-0 flex h-full w-[320px] flex-col bg-white"
                >
                  <ShowFirstMatching>
                    <Show when={isLoadingBlockAudit}>
                      <ActivityLoading />
                    </Show>
                    <Show when={!isLoadingBlockAudit}>
                      <div className="flex h-full w-full flex-col overflow-y-auto border-l border-gray-200 bg-white">
                        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                          <h2 className="text-base font-normal text-black">Activity</h2>
                          <XIcon
                            className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700"
                            onClick={toggleVisibilityOfActivityLog}
                          />
                        </div>
                        {(() => {
                          const mapped = (blockAudit ?? []).map(mapAuditItemUser);
                          const users = deriveUsers(mapped);
                          const userKey = (e: (typeof mapped)[0]) => e.user?.id ?? e.user?.name;
                          const filtered =
                            filterKey === EVERYONE ? mapped : mapped.filter((e) => userKey(e) === filterKey);
                          const ordered = sort === 'oldest' ? [...filtered].reverse() : filtered;
                          return (
                            <>
                              <ActivityControls
                                users={users}
                                filterKey={filterKey}
                                onFilter={setFilterKey}
                                sort={sort}
                                onToggleSort={() => setSort((s) => (s === 'newest' ? 'oldest' : 'newest'))}
                              />
                              <div className="overflow-x-hidden overflow-y-auto">
                                {groupByDay(ordered, new Date()).flatMap((group) => [
                                  <div
                                    key={`heading-${group.label}`}
                                    className="border-b border-gray-200 bg-gray-50 px-5 py-2 text-xs font-medium text-gray-500 uppercase"
                                  >
                                    {group.label}
                                  </div>,
                                  ...group.entries.map((entry) => (
                                    <ActivityEntry
                                      key={entry.id}
                                      entry={entry}
                                      blocks={[]}
                                      sections={[]}
                                      teams={teams ?? []}
                                    />
                                  )),
                                ])}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </Show>
                  </ShowFirstMatching>
                </motion.div>
              ) : (
                <motion.div
                  key="details"
                  {...rightPanelAnimationProps}
                  className="absolute top-0 right-0 flex h-full w-[320px] flex-col overflow-y-auto bg-white"
                >
                  <BlockConfigurationPanelController />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};
